import type {
  CreditBankEntry,
  CreditBankEntryProject,
  CreditBankReason,
  CreditBankSourceType,
} from '@/modules/account/types'
import { toCalendarDate } from '@/utils'

export interface CreditEntryGroup {
  key: number
  entries: CreditBankEntry[]
  totalDelta: number
  reason: CreditBankReason
  sourceType: CreditBankSourceType | null
  project: CreditBankEntryProject | null
  createdAt: string
}

const localDay = (isoDate: string) => toCalendarDate(new Date(isoDate))

const sourceTypeOf = (entry: CreditBankEntry) => entry.source?.type ?? null

const sameProject = (a: CreditBankEntry, b: CreditBankEntry) =>
  (a.project?.id ?? null) === (b.project?.id ?? null)

const belongsToGroup = (group: CreditEntryGroup, entry: CreditBankEntry) => {
  if (entry.expiresAt || group.entries[0].expiresAt) return false
  if (localDay(group.createdAt) !== localDay(entry.createdAt)) return false
  if (group.reason !== entry.reason) return false
  if (group.sourceType !== sourceTypeOf(entry)) return false
  if (group.sourceType !== 'statement') return true

  return sameProject(group.entries[0], entry)
}

const startGroup = (entry: CreditBankEntry): CreditEntryGroup => ({
  key: entry.id,
  entries: [entry],
  totalDelta: entry.deltaCredits,
  reason: entry.reason,
  sourceType: sourceTypeOf(entry),
  project: entry.project ?? null,
  createdAt: entry.createdAt,
})

export const groupEntries = (entries: CreditBankEntry[]) =>
  entries.reduce<CreditEntryGroup[]>((groups, entry) => {
    const current = groups[groups.length - 1]

    if (current && belongsToGroup(current, entry)) {
      current.entries.push(entry)
      current.totalDelta += entry.deltaCredits
      return groups
    }

    groups.push(startGroup(entry))
    return groups
  }, [])
